import { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { HelpSupportContext } from '@/contexts/helpSupportContext';
import { WARootState } from '@/store/store';
import { Role } from '@/models/AccountEnums';
import type { AdvisorGroupSettingModel } from '@/models/AppViewModel';
import {
  useContactSupportMutation,
  useGetContactSupportTypesQuery,
} from '@/store/authApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { capitalizeFirstLetter } from '@/utils/string';
import { isCapacitorApp } from '@/utils/isCapacitorApp';

/** Stable empty arrays for selector/query defaults to avoid unnecessary rerenders */
const EMPTY_AG_SETTINGS: AdvisorGroupSettingModel[] = [];
const EMPTY_ISSUE_TYPES: string[] = [];

const toBoolean = (value?: string): boolean => {
  const normalized = (value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const isMobileDeviceBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

export default function HelpSupportModal() {
  const { open, initialIssueType, handleOpen } = useContext(HelpSupportContext);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const location = useLocation();
  const isAuthLoaded = useAuthIsLoaded();
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );
  const userName = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserName,
  );
  const appAdvisorGroupSettings = useSelector(
    (state: WARootState) =>
      state.appView.UserInfo?.AGSettings ?? EMPTY_AG_SETTINGS,
  );
  const isImpersonation = useSelector((state: WARootState) =>
    Boolean(state.appView.UserInfo?.IsImpersonation),
  );

  const shouldFetchHelpData = isAuthLoaded && open;
  const { data } = useGetContactSupportTypesQuery(
    shouldFetchHelpData ? undefined : skipToken,
  );
  const issueTypes = data ?? EMPTY_ISSUE_TYPES;

  const issueTypeOptions = useMemo(() => {
    const labelMapping: Record<string, string> = {
      'login/password': 'Login and password support',
      'account connection': 'Account connection',
      'display issue': 'Display issue',
      'data issue (incorrect/missing)': 'Data issue (incorrect/missing)',
      other: 'Other',
    };
    const toOption = (option: string) => ({
      label: labelMapping[option]
        ? labelMapping[option]
        : capitalizeFirstLetter(option),
      value: option,
    });
    const options = issueTypes.map(toOption);
    if (initialIssueType && !issueTypes.includes(initialIssueType)) {
      options.unshift(toOption(initialIssueType));
    }
    return options;
  }, [issueTypes, initialIssueType]);

  const [contactSupport, { isLoading: isSubmitting }] =
    useContactSupportMutation();

  const [description, setDescription] = useState<string>('');
  const [issueType, setIssueType] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const useNativeIssueTypeSelect =
    isCapacitorApp() || (isCoarsePointer && isMobileDeviceBrowser());

  const canShowHelpMenu = useMemo(() => {
    const role = currentRole;
    if (!role) {
      return false;
    }

    const isInvestorFacingRole =
      role === Role.Investor || role === Role.Estate || role === Role.Owner;

    if (!isInvestorFacingRole) {
      return true;
    }

    if (!isAuthLoaded) {
      return false;
    }

    const getSetting = (key: string) =>
      appAdvisorGroupSettings.find((setting) => setting.Key === key)?.Value ??
      '';

    const investorShowContactSupport = toBoolean(
      getSetting('InvestorShowContactSupport'),
    );
    const investorHelpOption = Number(getSetting('InvestorHelpOption')) || 1;
    const isDoNotUseHelp = investorHelpOption === 4;

    return investorShowContactSupport && !isDoNotUseHelp;
  }, [appAdvisorGroupSettings, currentRole, isAuthLoaded]);

  useEffect(() => {
    if (open && !canShowHelpMenu) {
      handleOpen(false);
    }
  }, [canShowHelpMenu, handleOpen, open]);

  useEffect(() => {
    if (open && initialIssueType) {
      setIssueType(initialIssueType);
    }
  }, [open, initialIssueType]);

  const handleClose = () => {
    setSubmitError('');
    setDescription('');
    setIssueType('');
    handleOpen(false);
  };

  const isFormValid = description.trim().length > 0 && !isSubmitting;

  const handleIssueTypeChange = (event: SelectChangeEvent) => {
    setIssueType(event.target.value);
  };

  const handleSubmit = async () => {
    setSubmitError('');

    if (!description.trim()) {
      setSubmitError('Description is required.');
      return;
    }

    if (!currentRole || !userName) {
      setSubmitError('Unable to submit right now. Please try again.');
      return;
    }

    try {
      await contactSupport({
        Description: description,
        currentRole,
        userName,
        isImpersonation,
        requestorController: location.pathname,
        type: issueType || 'other',
      }).unwrap();

      handleClose();
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    }
  };

  return (
    <Dialog
      open={open && canShowHelpMenu}
      onClose={handleClose}
      fullWidth
      fullScreen={isXs}
      maxWidth='xs'
      slotProps={{
        paper: isXs
          ? {
              sx: {
                m: 0,
                borderRadius: 0,
                height: '100%',
                paddingTop:
                  'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
                paddingBottom:
                  'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
              },
            }
          : undefined,
      }}
    >
      <DialogTitle>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography variant='h6'>Help & Support</Typography>
          <IconButton
            onClick={handleClose}
            size='small'
            aria-label='Close help modal'
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiDialogTitle-root + &, .MuiDialogTitle-root + &.MuiDialogContent-root':
            {
              padding: '0 24px 32px 24px !important',
            },
        }}
      >
        <Typography variant='body2'>
          Describe your issue and our support team will review it.
        </Typography>

        <TextField
          id='help-support-description'
          label='Description'
          placeholder='Describe your issue...'
          multiline
          rows={4}
          fullWidth
          value={description}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setDescription(event.target.value);
          }}
          required
        />

        {useNativeIssueTypeSelect ? (
          <TextField
            id='help-support-issue-type'
            select
            fullWidth
            label='Issue Type (optional)'
            value={issueType}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setIssueType(event.target.value);
            }}
            slotProps={{ select: { native: true } }}
          >
            <option value=''></option>
            {issueTypeOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </TextField>
        ) : (
          <FormControl fullWidth>
            <InputLabel id='help-support-issue-type-label'>
              Issue Type (optional)
            </InputLabel>
            <Select
              labelId='help-support-issue-type-label'
              id='help-support-issue-type'
              value={issueType}
              label='Issue Type (optional)'
              onChange={handleIssueTypeChange}
            >
              {issueTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {submitError && <Alert severity='error'>{submitError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!isFormValid}
          onClick={() => void handleSubmit()}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
